/**
 * @fileoverview Mock Series Generators
 * Provides deterministic mock data for liquidity series.
 * @module store/liquidity/mock_series
 *
 * CONTRACT:
 * - All generators are pure functions: same range always yields same output
 * - Determinism is achieved via seeded PRNG with fixed seeds per series type
 * - ANCHOR_DATE is the reference point for date generation
 * - Supported time ranges: "1M", "3M", "6M", "1Y", "2Y"
 * - Unsupported ranges default to "1Y"
 */

/**
 * A single point in a time series.
 * @typedef {Object} TimeseriesPoint
 * @property {string} t - ISO 8601 date string (e.g., "2025-01-15")
 * @property {number} v - Numeric value at this point in time
 */

/**
 * Anchor date for deterministic data generation.
 * All dates are computed relative to this fixed point.
 * @type {Date}
 */
const ANCHOR_DATE = new Date("2025-01-01T00:00:00Z");

/**
 * Maps time range strings to number of days.
 * @type {Record<string, number>}
 */
const TIME_RANGE_DAYS = {
  "1M": 30,
  "3M": 90,
  "6M": 180,
  "1Y": 365,
  "2Y": 730,
};

/**
 * Default time range if an unsupported value is provided.
 * @type {string}
 */
const DEFAULT_TIME_RANGE = "1Y";

/**
 * Seeds for deterministic random number generation.
 * Each series type has a unique seed for reproducibility.
 * @type {Object}
 */
const SERIES_SEEDS = {
  dxy: 42,
  basis: 137,
  liquidity: 271,
};

/**
 * Simple deterministic pseudo-random number generator (seeded LCG).
 * Uses Linear Congruential Generator algorithm for reproducibility.
 *
 * @param {number} seed - Seed value (integer)
 * @returns {function(): number} Function returning pseudo-random number in [0, 1)
 *
 * @example
 * const random = createSeededRandom(42);
 * console.log(random()); // Always returns same sequence for seed 42
 */
function createSeededRandom(seed) {
  let state = seed;
  return function () {
    // Linear congruential generator constants (glibc)
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Generates an array of ISO date strings going backwards from anchor.
 * Dates are returned in chronological order (oldest first).
 *
 * @param {string} range - Time range key (e.g., "1Y", "6M")
 * @returns {string[]} Array of ISO date strings (YYYY-MM-DD format)
 *
 * @example
 * generateDateRange("1M"); // Returns 30 date strings ending at ANCHOR_DATE
 */
export function generateDateRange(range) {
  const days = TIME_RANGE_DAYS[range] || TIME_RANGE_DAYS[DEFAULT_TIME_RANGE];
  const dates = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(ANCHOR_DATE);
    date.setDate(date.getDate() - i);
    dates.push(date.toISOString().split("T")[0]);
  }

  return dates;
}

/**
 * Generates mock DXY (Dollar Index) series.
 * Simulates realistic DXY movement around 100-110 range with random walk
 * and slight mean reversion toward 104.
 *
 * @param {string} range - Time range key (e.g., "1Y", "6M")
 * @returns {TimeseriesPoint[]} DXY time series, oldest first
 *
 * @example
 * const series = generateMockDxySeries("1Y");
 * // Returns 365 points with DXY values ~95-115
 */
export function generateMockDxySeries(range) {
  const dates = generateDateRange(range);
  const random = createSeededRandom(SERIES_SEEDS.dxy);

  let value = 103.5; // Starting DXY value
  const volatility = 0.15; // Daily volatility
  const meanReversionRate = 0.01;
  const meanReversionTarget = 104;

  return dates.map((t) => {
    // Random walk with slight mean reversion toward target
    const meanReversion = (meanReversionTarget - value) * meanReversionRate;
    const noise = (random() - 0.5) * volatility;
    value = value + meanReversion + noise;

    // Clamp to realistic range
    value = Math.max(95, Math.min(115, value));

    return {
      t,
      v: Math.round(value * 100) / 100,
    };
  });
}

/**
 * Generates mock cross-currency basis swap series.
 * Simulates basis in basis points (typically negative during stress).
 * Includes cyclical stress patterns to simulate market conditions.
 *
 * @param {string} range - Time range key (e.g., "1Y", "6M")
 * @returns {TimeseriesPoint[]} Basis swap time series in bps, oldest first
 *
 * @example
 * const series = generateMockBasisSeries("6M");
 * // Returns 180 points with basis values ~-100 to +20 bps
 */
export function generateMockBasisSeries(range) {
  const dates = generateDateRange(range);
  const random = createSeededRandom(SERIES_SEEDS.basis);

  let value = -15; // Starting basis in bps (slight dollar premium)
  const volatility = 3; // Daily volatility in bps
  const meanReversionRate = 0.02;
  const meanReversionTarget = -12;

  return dates.map((t, index) => {
    // Add some cyclical stress patterns
    const cyclicalStress = Math.sin(index / 30) * 5;
    const noise = (random() - 0.5) * volatility;
    const meanReversion = (meanReversionTarget - value) * meanReversionRate;

    value = value + meanReversion + noise + cyclicalStress * 0.1;

    // Clamp to realistic range (-100 to +20 bps)
    value = Math.max(-100, Math.min(20, value));

    return {
      t,
      v: Math.round(value * 100) / 100,
    };
  });
}

/**
 * Generates mock global USD liquidity series.
 * Simulates BIS-style global USD credit in trillions.
 * Includes slight upward trend to reflect typical credit expansion.
 *
 * @param {string} range - Time range key (e.g., "1Y", "6M")
 * @returns {TimeseriesPoint[]} USD liquidity time series in trillions, oldest first
 *
 * @example
 * const series = generateMockUsdLiquiditySeries("1Y");
 * // Returns 365 points with liquidity values ~10-18 trillion
 */
export function generateMockUsdLiquiditySeries(range) {
  const dates = generateDateRange(range);
  const random = createSeededRandom(SERIES_SEEDS.liquidity);

  let value = 12.5; // Starting value in trillions
  const trendPerDay = 0.001; // Slight upward trend
  const volatility = 0.03; // Daily volatility

  return dates.map((t) => {
    // Trend + noise
    const noise = (random() - 0.5) * volatility;
    value = value + trendPerDay + noise;

    // Clamp to realistic range
    value = Math.max(10, Math.min(18, value));

    return {
      t,
      v: Math.round(value * 1000) / 1000,
    };
  });
}
